<template>
    <RadioGroup
        id="shipping-options"
        v-if="shippingOptions && Object.keys(shippingOptions).length > 0"
        v-model="selectedShippingOption"
        class="mb-1"
    >
        <RadioGroupLabel class="font-semibold text-gray-500 flex items-center">
            Delivery to
            <span class="ml-1 flex items-center">
                <svg-vue
                    class="h-3 w-4 mr-1 shadow shadow-sm"
                    :icon="flag"
                    :key="flag"
                    aria-hidden="true"
                />
                {{ region }}
            </span>
            :
        </RadioGroupLabel>

        <div
            class="mt-4 md:mt-2 bg-white rounded-md -space-y-px"
            @click="updateShippingOption(selectedShippingOption)"
        >
            <RadioGroupOption
                :id="`shipping-option-${shippingOption.id}`"
                as="template"
                v-for="(shippingOption, shippingOptionId) in shippingOptions"
                :key="shippingOption.id"
                :value="shippingOption.id"
                v-slot="{ checked }"
            >
                <div
                    :class="[
                        shippingOptionId === 0
                            ? 'rounded-tl-md rounded-tr-md'
                            : '',
                        shippingOptionId === shippingOptions.length - 1
                            ? 'rounded-bl-md rounded-br-md'
                            : '',
                        checked
                            ? 'bg-emerald-50 border-nutreance-green-lighter z-10'
                            : 'border-gray-300',
                        'relative border p-4 flex cursor-pointer focus:outline-none',
                    ]"
                >
                    <LoadingIcon
                        v-if="loading && checked"
                        class="h-4 w-4 mt-0.5 text-nutreance-green"
                    />

                    <span
                        v-else
                        :class="[
                            checked
                                ? 'bg-nutreance-green border-transparent'
                                : 'bg-white border-gray-300',
                            'h-4 w-4 mt-0.5 cursor-pointer rounded-full border flex items-center justify-center shrink-0',
                        ]"
                        aria-hidden="true"
                    >
                        <span class="rounded-full bg-white w-1.5 h-1.5" />
                    </span>

                    <div class="ml-3 flex flex-col">
                        <RadioGroupLabel
                            as="span"
                            :class="[
                                checked ? 'text-emerald-700' : 'text-gray-500',
                                'block text-sm',
                            ]"
                        >
                            {{ shippingOption.summary }}
                        </RadioGroupLabel>
                    </div>
                </div>
            </RadioGroupOption>
        </div>
    </RadioGroup>
</template>

<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { useShippingOption } from "../../functions/useShippingOption";
import { RadioGroup, RadioGroupLabel, RadioGroupOption } from "@headlessui/vue";
import LoadingIcon from "../LoadingIcon.vue";

const props = defineProps<{
    country?: any;
    region?: string;
    shippingTimeId?: number;
    shippingOptions?: any;
}>();

const emit = defineEmits<{
    (e: "shippingTimeUpdated", shippingTimeId: number | undefined): void;
}>();

const { loading, updateShippingOption: updateShippingOptionRequest } =
    useShippingOption();

// FIXME(4f): `selectedShippingOption` derives from the optional `shippingTimeId`
// prop, so it can be `undefined`, but `useShippingOption().updateShippingOption`
// requires a `number`. The template fires this on `@click`, where a radio is
// always selected, so this guard preserves behavior while satisfying the type.
function updateShippingOption(shippingTimeId: number | undefined): void {
    if (shippingTimeId === undefined) {
        return;
    }

    updateShippingOptionRequest(shippingTimeId);
}

const selectedShippingOption = ref(props.shippingTimeId);
const flag = computed(() => `flags/${props.country.code.toLowerCase()}`);

watch(
    () => selectedShippingOption.value,
    (shippingTimeId) => {
        emit("shippingTimeUpdated", shippingTimeId);
    },
);

watch(
    () => props.shippingTimeId,
    (shippingTimeId) => {
        selectedShippingOption.value = shippingTimeId;
    },
);
</script>
